import React, { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Home, ShieldCheck, Mail, Lock, AlertTriangle } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        // 🚨 UPDATE: Dynamic Role-Based Redirection 🚨
        const userRole = result.user?.user_type?.toLowerCase() || result.user?.role?.toLowerCase();

        if (userRole === 'admin') {
          navigate('/admin'); // Admin goes to Admin Dashboard
        } else if (userRole === 'staff' || userRole === 'employee') {
          navigate('/employee'); // Staff/Employee goes to Employee Dashboard
        } else {
          navigate('/'); // Fallback to Home
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Server se connect nahi ho pa raha hai.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#050B14] px-4 py-8 relative overflow-hidden font-sans">
      
      {/* 🌟 LUXURIOUS CYBER BACKGROUND 🌟 */}
      {/* Cyber Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none z-0" style={{ backgroundImage: `linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />

      {/* Ambient Glows */}
      <div className="absolute top-0 -left-20 w-[400px] h-[400px] bg-[#1E5CB3]/20 rounded-full blur-[120px] pointer-events-none z-0" />
      <div className="absolute bottom-0 -right-20 w-[400px] h-[400px] bg-[#F7941D]/15 rounded-full blur-[120px] pointer-events-none z-0" />

      {/* 🌟 TOP LEFT: BACK TO HOME BUTTON 🌟 */}
      <div className="absolute top-6 left-6 z-20">
        <Link
          to="/"
          className="flex items-center gap-2 text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 backdrop-blur-md px-4 py-2 rounded-full text-[11px] font-bold transition-all duration-300 group shadow-sm"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1 text-[#F7941D]" />
          <span className="uppercase tracking-widest">Back to Site</span>
        </Link>
      </div>

      {/* 🌟 LOGIN CARD (Premium Vault Design) 🌟 */}
      <div className="relative z-10 w-full max-w-md">
        
        {/* Outer Laser Gradient Frame */}
        <div className="absolute -inset-0.5 rounded-[2rem] bg-gradient-to-r from-[#0D3F7F] via-[#F7941D] to-[#0D3F7F] opacity-30 blur-lg pointer-events-none" />

        <div className="bg-[#0A1326]/90 backdrop-blur-2xl border border-white/10 rounded-[2rem] shadow-[0_20px_60px_rgba(0,0,0,0.5)] p-8 sm:p-10 relative overflow-hidden">
          
          {/* Inner ambient glow */}
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#F7941D]/10 rounded-full blur-[60px]" />

          {/* Header */}
          <div className="text-center mb-8 relative z-10">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#0D3F7F]/40 border border-[#1E5CB3]/30 shadow-inner mb-5">
              <ShieldCheck className="w-7 h-7 text-[#F7941D]" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
              Devdeepam <span className="text-[#F7941D]">Portal</span>
            </h1>
            <p className="text-slate-400 text-[11px] mt-1.5 uppercase tracking-[0.2em] font-bold">
              Secure Staff & Admin Access
            </p>
          </div>

          {/* Error Message Box */}
          {error && (
            <div className="mb-6 p-3.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-[12px] font-bold flex items-start gap-2 relative z-10">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
            
            <div className="relative group">
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">
                Corporate Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-[#F7941D] transition-colors" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@devdeepam.com"
                  className="w-full bg-[#050B14] border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-white placeholder-slate-600 focus:outline-none focus:border-[#F7941D] focus:ring-1 focus:ring-[#F7941D] text-[13px] font-semibold transition-all shadow-inner"
                  required
                />
              </div>
            </div>

            <div className="relative group">
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">
                Access Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-[#F7941D] transition-colors" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-[#050B14] border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-white placeholder-slate-600 focus:outline-none focus:border-[#F7941D] focus:ring-1 focus:ring-[#F7941D] text-[13px] font-semibold transition-all shadow-inner"
                  required
                />
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full relative overflow-hidden rounded-xl p-[2px] font-bold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 group cursor-pointer"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-[#F7941D] via-[#FFC107] to-[#F7941D] opacity-70 group-hover:opacity-100 transition-opacity animate-pulse" />
                <span className="relative flex items-center justify-center bg-gradient-to-r from-[#F7941D] to-[#E67A00] text-white text-[13px] font-black py-3.5 px-4 rounded-[10px] tracking-widest uppercase shadow-inner">
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Authenticating...
                    </span>
                  ) : (
                    'Secure Login'
                  )}
                </span>
              </button>
            </div>
          </form>

          {/* Footer Link to Home */}
          <div className="mt-8 pt-6 border-t border-white/10 text-center relative z-10">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-[#F7941D] font-bold uppercase tracking-wider transition-colors"
            >
              <Home className="w-3.5 h-3.5" />
              <span>Return to Public Website</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;